import { Component, HostListener, OnInit } from '@angular/core';
import { Kpi } from '../../../model/kpi';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../services/tenants.service';
import { AuthGuard } from '../../../common/auth-guard.service';
import { KpisService } from '../../../services/kpis.service';
import { CommonMessagesService } from '../../../services/common-messages.service';
import { AppConfig } from '../../../shared/global-variables';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { KpiPersistRequest } from '../../../model/requests/kpi-persist-request';
import { Organization } from '../../../model/organization';
import { LoggedUser } from '../../../common/logged-user.service';
import { DeviceMeasurement } from '../../../model/device-measurement';
import { AssetMeasurement } from '../../../model/asset-measurement';
import { KpiMeasurement } from '../../../model/kpi-measurement';
import { DevicesService } from "../../../services/devices.service";
import { AssetsService } from "../../../services/assets.service";
import { KpiPersistRequestEnabledFilters } from "../../../model/requests/kpi-persist-request-enabled-filters";
import { KpiPersistRequestFilters } from "../../../model/requests/kpi-persist-request-filters";
import {MeasurementType} from "../../../model/measurement-type";

@Component({
  selector: 'app-kpis-form',
  templateUrl: './kpis-form.component.html',
  styleUrls: ['./kpis-form.component.scss']
})
export class KpisFormComponent implements OnInit {

  public kpi: Kpi;
  public tenants: Tenant[];
  public organizations: Organization[];
  public measurementTypes: MeasurementType[];

  public kpiInfoForm: FormGroup;
  public filters: KpiPersistRequestFilters;
  public enabledFilters: KpiPersistRequestEnabledFilters;
  public deviceMeasurements: DeviceMeasurement[];
  public assetMeasurements: AssetMeasurement[];
  public kpis: Kpi[];

  public items: any[];
  public currentStep: number;
  public stepChanged: boolean;

  public persistingChanges: any;
  public formSuccess: boolean;
  public formLoaded: boolean;

  public mode: 'create' | 'edit';
  public creationMode: 'SINGLE' | 'MULTIPLE';

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private i18n: TranslocoService,
    private tenantsService: TenantsService,
    private authGuard: AuthGuard,
    private devicesService: DevicesService,
    private assetsService: AssetsService,
    private kpisService: KpisService,
    private commonMessagesService: CommonMessagesService,
    private loggedUser: LoggedUser
  ) {
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
    this.formLoaded = false;
    this.currentStep = 0;
    this.filters = {
      deviceMeasurementFilters: { name: null, tenantUuids: [], organizationUuids: [], measurementTypeSensors: [] },
      assetMeasurementFilters: { name: null, tenantUuids: [], organizationUuids: [], measurementTypeSensors: [] },
      kpiFilters: { name: null, tenantUuids: [], organizationUuids: [] }
    };
    this.enabledFilters = {
      deviceMeasurementFilters: false,
      assetMeasurementFilters: false,
      kpiFilters: false
    };
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  private loadCreationForm(): void {
    this.buildStepItems();
    this.kpi = new Kpi();
    if (!this.authGuard.superAdminRequired()) {
      this.kpi.tenant = this.tenants[0];
    }
    this.createForm(this.kpi);
    this.formLoaded = true;
  }

  private loadEditionForm(uuid): void {
    this.items = [
      {id: 'info', icon: 'pi pi-chart-pie', label: 'model.kpi', description: 'forms.descriptions.kpi'},
      {id: 'confirmation', icon: 'pi pi-check', label: 'forms.confirmation'}
    ];
    this.kpisService.getKpi(uuid).subscribe(response => {
      this.kpi = response.kpi;
      this.tenants = [this.kpi.tenant];
      this.createForm(this.kpi);
      this.formLoaded = true;
    });
  }

  public clearMeasurements(): void {
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
  }

  public onTenantChange(tenant: Tenant) {
    this.clearMeasurements();
    if (tenant) {
      this.kpiInfoForm.get('organization').setValue(null);
      this.kpiInfoForm.get('organization').disable();
      this.loadOrganizations(tenant.uuid).then(() => {
        this.kpiInfoForm.get('organization').enable();
      });
      this.kpiInfoForm.get('measurementType').setValue(null);
      this.kpiInfoForm.get('measurementType').disable();
      this.loadMeasurementTypes(tenant.uuid);
    }
  }

  public onNextStep(): void {
    this.stepChanged = true;
    if (this.currentStep < this.items.length - 1) {
      this.currentStep++;
    }
  }

  public onPreviousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  public onCancel(): void {
    this.router.navigate(([`/kpis`]));
  }

  private createForm(kpi: Kpi): void {
    if (!this.creationMode) {
      this.creationMode = 'SINGLE';
    }

    this.kpiInfoForm = this.formBuilder.group({
      uuid: [{value: kpi.uuid, disabled: true}],
      name: [kpi.name, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [kpi.description, [Validators.maxLength(255)]],
      measurementType: [{value: kpi.measurementType, disabled: true}, [Validators.maxLength(255)]],
      timePeriod: [kpi.timePeriod, [Validators.maxLength(255), Validators.required]],
      operation: [kpi.operation, [Validators.maxLength(255), Validators.required]],
      creationMode: [this.creationMode, [Validators.maxLength(255)]],
      tenant: [kpi.tenant, [Validators.required]],
      organization: [{value: kpi.organization, disabled: true}],
      timezone: [kpi.timezone, [Validators.required]],
      calculateIfMissingData: [kpi.calculateIfMissingData]
    });
    this.kpiInfoForm.markAsPending();

    if (this.mode === 'edit' || !this.authGuard.superAdminRequired()) {
      this.kpiInfoForm.get('tenant').disable();
      this.loadOrganizations(kpi.tenant.uuid).then(() => {
        this.loggedUser.userObservable.subscribe(user => {
          if (!user.organizationUuid) {
            this.kpiInfoForm.get('organization').enable();
          } else {
            this.kpiInfoForm.get('organization').setValue(this.organizations[0]);
            this.kpiInfoForm.get('organization').disable();
          }
        });
      });
      this.loadMeasurementTypes(kpi.tenant.uuid);
    }
  }

  private loadOrganizations(tenantUuid: string): Promise<void> {
    return this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise().then(value => {
      this.organizations = value.organizations;
    });
  }

  private loadMeasurementTypes(tenantUuid: string): void {
    this.tenantsService.getTenantMeasurementTypes(tenantUuid).toPromise().then(response => {
      this.measurementTypes = response.measurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.kpiInfoForm.get('measurementType').enable();
    });
  }

  public persistKpiData(): void {
    if (!this.kpiInfoForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    if (this.mode === 'create') {
      const totalPromises: Promise<any>[] = [Promise.resolve(), Promise.resolve(), Promise.resolve()];
      if (persistData.enabledFilters.deviceMeasurementFilters) {
        totalPromises[0] = this.devicesService
          .getCommonDeviceMeasurements(persistData.filters.deviceMeasurementFilters, 0, 1).toPromise();
      }
      if (persistData.enabledFilters.assetMeasurementFilters) {
        totalPromises[1] = this.assetsService
          .getCommonAssetMeasurements(persistData.filters.assetMeasurementFilters, 0, 1).toPromise();
      }
      if (persistData.enabledFilters.kpiFilters) {
        totalPromises[2] = this.kpisService
          .getKpis(0, 1, persistData.filters.kpiFilters).toPromise();
      }

      Promise.all(totalPromises).then(values => {
        const totalData = {
          filteredDeviceMeasurements: values[0]?.totalElements ?? 0,
          filteredAssetMeasurements: values[1]?.totalElements ?? 0,
          filteredKpis: values[2]?.totalElements ?? 0,
          deviceMeasurements: this.deviceMeasurements.length,
          assetMeasurements: this.assetMeasurements.length,
          kpis: this.kpis.length
        };
        this.commonMessagesService.showCreateKpiConfirmation(
          () => this.persistKpi(persistData),
          () => this.persistingChanges = false,
          totalData
        );
      })

    } else {
      this.persistKpi(persistData);
    }
  }

  private persistKpi(persistData: KpiPersistRequest) {
    let promise;
    if (this.mode === 'create') {
      promise = this.kpisService.createKpi(persistData).toPromise();
    } else {
      promise = this.kpisService.editKpi(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      if (persistData.creationMode === 'SINGLE' && this.mode === 'create') {
        this.router.navigate(['kpis', response.uuid, 'details']);
      } else {
        this.router.navigate(['kpis']);
      }
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): KpiPersistRequest {
    const formValue = this.kpiInfoForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      oldUuid: formValue.uuid,
      operation: formValue.operation,
      organizationUuid: formValue.organization?.uuid ?? null,
      tenantUuid: formValue.tenant.uuid,
      timezone: formValue.timezone,
      calculateIfMissingData: formValue.calculateIfMissingData ?? false,
      creationMode: formValue.creationMode,
      timePeriod: formValue.timePeriod,
      filters: this.filters,
      enabledFilters: this.enabledFilters,
      measurements: this.buildKpiMeasurements(formValue.tenant.uuid),
      sensor: formValue.measurementType?.sensor ?? null
    };
  }

  private buildKpiMeasurements(tenantUuid: string): KpiMeasurement[] {
    const measurements: KpiMeasurement[] = [];
    this.deviceMeasurements.forEach((deviceMeasurement: DeviceMeasurement) => measurements.push({
      tenantUuid,
      entityUuid: deviceMeasurement.device.uuid,
      measurementUuid: deviceMeasurement.uuid,
      type: 'DEVICE_MEASUREMENT'
    }));
    this.assetMeasurements.forEach((assetMeasurement: AssetMeasurement) => measurements.push({
      tenantUuid,
      entityUuid: assetMeasurement.asset.uuid,
      measurementUuid: assetMeasurement.uuid,
      type: 'ASSET_MEASUREMENT'
    }));
    this.kpis.forEach((kpi: Kpi) => measurements.push({
      tenantUuid,
      entityUuid: kpi.uuid,
      measurementUuid: null,
      type: 'KPI'
    }));
    return measurements;
  }

  private buildStepItems() {
    this.items = [
      {
        id: 'info',
        icon: 'pi pi-chart-pie',
        label: 'model.kpi',
        description: 'forms.descriptions.kpi'
      },
      {
        id: 'filteredMeasurements',
        icon: 'pi pi-wifi',
        label: 'forms.sensorsByFilters',
        description: 'forms.descriptions.sensorsByFilters'
      },
      {
        id: 'measurements',
        icon: 'pi pi-plus',
        label: 'forms.additionalSensors',
        description: 'forms.descriptions.additionalSensors'
      },
      {
        id: 'confirmation',
        icon: 'pi pi-check',
        label: 'forms.confirmation'
      }
    ];
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.kpiInfoForm.untouched;
  }

}
